/**
 * 光属性カードのトリガー実装
 *
 * このファイルには光属性カードの個別トリガー効果を実装します。
 * 各カードは独自のトリガー定義を持ち、triggerEngineによって管理されます。
 */

import { TRIGGER_TYPES, ACTIVATION_TYPES, TRIGGER_PRIORITIES } from '../triggerTypes';
import type { TriggerContext } from '../triggerEngine';
import type { CardWithId, MonsterInstance } from '../../types/card';
import type { TriggerDefinition } from '../../types/effect';
import {
  getPlayerContext,
  millDeck,
  conditionalDamage,
  searchCard,
  reviveFromGraveyard,
  drawCards,
  healLife,
  destroyMonster,
  modifyAttack,
  modifyHP,
} from '../effectHelpers';
import { hasCategory } from '../../utils/helpers';
import { executeZankonEffect } from '../keywordAbilities';
import { STATUS_EFFECT_TYPES, statusEffectEngine } from '../statusEffects';

/**
 * 光属性カードのトリガー定義
 * カードID => トリガー配列のマッピング
 */
export const lightCardTriggers = {
  /**
   * C0000020: 灯火の護衛霊
   * 【常時】自分の光属性モンスターが受けるダメージを200軽減する（1ターンに1度）。
   *
   * 注: この効果は continuousEffects/effectDefinitions/monsterCards.js で DAMAGE_REDUCTION として実装済み。
   *     トリガーシステムではなく常時効果システムで処理されるため、ここでの実装は不要。
   */
  // C0000020のトリガーは不要（常時効果システムで実装済み）

  /**
   * C0000056: 輝聖女ルミナス
   * 【エンドフェイズ時】相手のモンスターの攻撃力を200ダウン。
   */
  C0000056: [
    {
      type: TRIGGER_TYPES.ON_END_PHASE_SELF,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: 'エンドフェイズ: 相手モンスター1体の攻撃力-200',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        const { opponentField } = getPlayerContext(context);
        const opponentMonsters = opponentField.filter((m) => m !== null);
        if (opponentMonsters.length === 0) {
          addLog('輝聖女ルミナスの効果: 相手フィールドにモンスターがいません', 'info');
          return;
        }

        // 最初のモンスターをターゲット
        const targetIndex = opponentField.findIndex((m) => m !== null);
        if (targetIndex !== -1) {
          modifyAttack(context as any, -200, targetIndex, true, false);
          addLog('輝聖女ルミナスの効果: 相手モンスターの攻撃力-200', 'info');
        }
      },
    },
  ],

  /**
   * C0000057: プリズム・ガーディアン
   * 【自分モンスター被ダメージ時】1ターンに1度だけ、自分の光属性モンスターがダメージを受ける場合、代わりにこのカードが受ける。
   */
  C0000057: [
    {
      type: TRIGGER_TYPES.ON_DAMAGE_RECEIVED,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '被ダメージ時: 光属性の代わりにダメージを受ける',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        addLog('プリズム・ガーディアンの効果: ダメージ肩代わり（未実装）', 'info');
        // TODO: ダメージ肩代わりシステムが必要
      },
    },
  ],

  /**
   * C0000058: 熾天使セラフ
   * 【相手モンスター召喚時】対象モンスターに200ダメージを与える。
   */
  C0000058: [
    {
      type: TRIGGER_TYPES.ON_OPPONENT_SUMMON,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '相手召喚時: 召喚されたモンスターに200ダメージ',
      effect: (context: TriggerContext) => {
        const { slotIndex, addLog } = context;
        addLog('熾天使セラフの効果: 召喚されたモンスターに200ダメージ', 'damage');
        // 召喚されたモンスター（相手フィールドのslotIndex）にダメージ
        conditionalDamage(context as any, 200, 'opponent_monster', slotIndex);
      },
    },
  ],

  /**
   * C0000059: 光の騎士
   * 【召喚時】デッキから《光の》魔法カード1枚を手札に加える。
   */
  C0000059: [
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '召喚時: 《光の》魔法カードをサーチ',
      effect: (context: TriggerContext) => {
        const found = searchCard(context as any, (card) => {
          return card.type === 'magic' && Boolean(card.name && card.name.includes('光の'));
        });

        if (!found) {
          context.addLog('光の騎士の効果: 《光の》魔法カードが見つかりませんでした', 'info');
        }
      },
    },
  ],

  /**
   * C0000061: 輝くユニコーン
   * 【自攻撃時】自分のライフを500回復。
   */
  C0000061: [
    {
      type: TRIGGER_TYPES.ON_ATTACK,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '攻撃時: 自分のライフ500回復',
      effect: (context: TriggerContext) => {
        healLife(context as any, 500, true);
      },
    },
  ],

  /**
   * C0000062: 光の使徒
   * 【自メインフェイズ時】このカードをリリースすると、自分のライフを400回復できる。
   */
  C0000062: [
    {
      type: TRIGGER_TYPES.ON_MAIN_PHASE_SELF,
      activationType: ACTIVATION_TYPES.OPTIONAL,
      description: 'メインフェイズ: リリースで400回復',
      effect: (context: TriggerContext) => {
        const { monsterIndex, addLog } = context;
        // このカードを破壊
        destroyMonster(context as any, monsterIndex ?? 0, false);
        // ライフ回復
        healLife(context as any, 400, true);
        addLog('光の使徒の効果: リリースして400回復', 'info');
      },
    },
  ],

  /**
   * C0000071: クリスタルサンクチュアリ
   * 【常時】光属性モンスターの攻撃力を500アップ。
   * 【常時】2体以上光属性モンスターが場にいるとき相手の基本技の効果を1ターンに1度無効化できる。
   * 【自エンドフェイズ時】自分のライフを500回復。
   */
  C0000071: [
    {
      type: TRIGGER_TYPES.CONTINUOUS,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '常時: 光属性の攻撃力+500',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        addLog('クリスタルサンクチュアリの常時効果: 光属性攻撃力+500（未実装）', 'info');
        // TODO: フィールドカードの常時効果システムが必要
      },
    },
    {
      type: TRIGGER_TYPES.ON_END_PHASE_SELF,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: 'エンドフェイズ: 自分のライフ500回復',
      effect: (context: TriggerContext) => {
        healLife(context as any, 500, true);
      },
    },
  ],

  /**
   * C0000072: ルミナスの聖域
   * 【光属性モンスターが破壊される時】HPを1にして、代わりに自分のSPを1減らして守ることができる（1ターンに1度）。
   *
   * 注: 実際の破壊防止処理は effectHelpers.js の checkFieldCardDestroyPrevention() で実装。
   *     このトリガーは登録用のみ（実際の発火は handleMonsterDestruction 内で行われる）。
   */
  C0000072: [
    {
      type: TRIGGER_TYPES.ON_ATTRIBUTE_BEFORE_DESTROY,
      activationType: ACTIVATION_TYPES.AUTOMATIC, // 自動発動（条件チェックは checkFieldCardDestroyPrevention 内で）
      description: '光属性破壊時: SP1消費でHP1で守る（1ターンに1度）',
      condition: { attribute: '光' },
      effect: (context: TriggerContext) => {
        // 実際の処理は effectHelpers.js の checkFieldCardDestroyPrevention() で実行
        // このトリガーは登録情報のみを提供
      },
    },
  ],

  /**
   * C0000073: 輝く天蓋
   * 【常時】相手モンスター全体の攻撃力を、場にいる光属性モンスター1体につき200ダウン。
   */
  C0000073: [
    {
      type: TRIGGER_TYPES.CONTINUOUS,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '常時: 相手全体の攻撃力を光属性×200ダウン',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        const { myField } = getPlayerContext(context);

        // 場の光属性モンスターをカウント
        const lightCount = myField.filter((monster) =>
          monster && monster.attribute === '光'
        ).length;

        if (lightCount > 0) {
          const attackDown = lightCount * 200;
          addLog(`輝く天蓋の効果: 相手全体の攻撃力-${attackDown}（未実装）`, 'info');
          // TODO: フィールドカードの常時効果システムが必要
        }
      },
    },
  ],

  /**
   * C0000091: 灯火の精霊
   * 【常時】自分の光属性モンスターが召喚されるたび、そのモンスターのHPを200アップ。
   */
  C0000091: [
    {
      type: TRIGGER_TYPES.ON_ATTRIBUTE_SUMMON_SELF,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      displayDescription: '自分光属性モンスター召喚時',
      description: '光属性召喚時: そのモンスターのHP+200',
      effect: (context: TriggerContext) => {
        const { card, slotIndex, addLog } = context;

        if (card && card.attribute === '光') {
          modifyHP(context as any, 200, slotIndex, false);
          addLog(`灯火の精霊の効果: ${card.name}のHP+200`, 'info');
        }
      },
    },
  ],

  /**
   * C0000121: 聖域の灯守兵
   * 【召喚時】自分の手札から光属性モンスター1体を公開し、デッキに戻す。その後、1枚ドローする。
   */
  C0000121: [
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.OPTIONAL,
      description: '召喚時: 光属性モンスター1枚をデッキに戻しドロー',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        const { myHand, setMyHand, setMyDeck } = getPlayerContext(context);

        // 手札から光属性モンスターを探す
        const lightMonster = myHand.find((card) =>
          card.type === 'monster' && card.attribute === '光'
        );

        if (!lightMonster) {
          addLog('聖域の灯守兵の効果: 手札に光属性モンスターがいません', 'info');
          return;
        }

        // 手札からデッキに戻す
        setMyHand((prev) => prev.filter((c) => c.uniqueId !== lightMonster.uniqueId));
        setMyDeck((prev) => [...prev, lightMonster]);

        // 1枚ドロー
        drawCards(context as any, 1);
        addLog(`${lightMonster.name}をデッキに戻し、1枚ドロー`, 'info');
      },
    },
  ],

  /**
   * C0000162: 死神天使ルシフェリエル
   * 【召喚時】自分の墓地の光属性モンスターを全てデッキに戻し、その枚数×500ライフを回復。
   * 【自壊時】相手プレイヤーに1500ダメージを与え、自分のライフを半分にする。
   */
  C0000162: [
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '召喚時: 墓地の光属性をデッキに戻し枚数×500回復',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        const { myGraveyard, setMyGraveyard, setMyDeck } = getPlayerContext(context);

        // 墓地から光属性モンスターを全て取得
        const lightMonsters = myGraveyard.filter((card) =>
          card.type === 'monster' && card.attribute === '光'
        );

        if (lightMonsters.length === 0) {
          addLog('死神天使ルシフェリエルの効果: 墓地に光属性モンスターがいません', 'info');
          return;
        }

        // 墓地からデッキに戻す
        setMyGraveyard((prev) => prev.filter((c) =>
          !(c.type === 'monster' && c.attribute === '光')
        ));
        setMyDeck((prev) => [...prev, ...lightMonsters]);

        // ライフ回復
        const healAmount = lightMonsters.length * 500;
        healLife(context as any, healAmount, true);
        addLog(`死神天使ルシフェリエルの効果: 光属性${lightMonsters.length}体をデッキに戻し、${healAmount}回復`, 'info');
      },
    },
    {
      type: TRIGGER_TYPES.ON_DESTROY_SELF,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '破壊時: 相手に1500、自分のライフ半分に',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        const { myLife, setMyLife } = getPlayerContext(context);

        // 相手に1500ダメージ
        conditionalDamage(context as any, 1500, 'opponent');

        // 自分のライフを半分に
        const newLife = Math.floor(myLife / 2);
        setMyLife(newLife);
        addLog(`死神天使ルシフェリエルの効果: 自分のライフが${newLife}になった`, 'damage');
      },
    },
  ],

  /**
   * C0000211: フルーツ・マリオネット・アップル
   * 【召喚時】デッキからコスト3以下のプラントモンスター1体を墓地に送る。
   * 【自分エンドフェイズ時】レスト状態のSPトークンを１つアクティブにする（ターンに1度のみ）。
   */
  C0000211: [
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '召喚時: コスト3以下のプラントを墓地に',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        const { myDeck, setMyDeck, setMyGraveyard } = getPlayerContext(context);

        // デッキからコスト3以下のプラントを探す
        const plantCard = myDeck.find((card) =>
          card.type === 'monster' &&
          hasCategory(card, '【プラント】') &&
          card.cost <= 3
        );

        if (!plantCard) {
          addLog('フルーツ・マリオネット・アップルの効果: 対象カードが見つかりませんでした', 'info');
          return;
        }

        // デッキから墓地に送る
        setMyDeck((prev) => prev.filter((c) => c.uniqueId !== plantCard.uniqueId));
        setMyGraveyard((prev) => [...prev, plantCard]);
        addLog(`${plantCard.name}を墓地に送った`, 'info');
      },
    },
    {
      type: TRIGGER_TYPES.ON_END_PHASE_SELF,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: 'エンドフェイズ: レストSP1個をアクティブに',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        const { myRestedSP, setMyRestedSP, setMyActiveSP } = getPlayerContext(context);

        if (myRestedSP > 0) {
          setMyRestedSP((prev) => prev - 1);
          setMyActiveSP((prev) => prev + 1);
          addLog('フルーツ・マリオネット・アップルの効果: レストSP1個をアクティブにした', 'info');
        }
      },
    },
  ],

  /**
   * C0000212: フルーツ・マリオネット・オレンジ
   * 【召喚時】デッキから《フルーツ・マリオネット》モンスター1体を手札に加える。
   */
  C0000212: [
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '召喚時: 《フルーツ・マリオネット》をサーチ',
      effect: (context: TriggerContext) => {
        const found = searchCard(context as any, (card) => {
          return card.type === 'monster' &&
                 Boolean(card.name && card.name.includes('フルーツ・マリオネット'));
        });

        if (!found) {
          context.addLog('フルーツ・マリオネット・オレンジの効果: 対象カードが見つかりませんでした', 'info');
        }
      },
    },
  ],

  /**
   * C0000213: フルーツ・マリオネット・バナナ
   * 【自壊時】自分モンスター全てのHPを600回復。
   */
  C0000213: [
    {
      type: TRIGGER_TYPES.ON_DESTROY_SELF,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '破壊時: 自分モンスター全てのHP+600',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        const { setMyField } = getPlayerContext(context);

        let healed = 0;
        setMyField((prev) => {
          return prev.map((monster) => {
            if (monster) {
              healed++;
              const maxHp = (monster as any).maxHp ?? monster.hp ?? monster.currentHp;
              const newHp = Math.min(monster.currentHp + 600, maxHp);
              return { ...monster, currentHp: newHp };
            }
            return monster;
          });
        });

        if (healed > 0) {
          addLog(`フルーツ・マリオネット・バナナの効果: ${healed}体のHPを600回復`, 'info');
        }
      },
    },
  ],

  /**
   * C0000214: フルーツ・マリオネット・グレープ
   * 【召喚時】自分の墓地の［プラント］モンスター1体を場に戻す（攻撃力は半分）。
   */
  C0000214: [
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '召喚時: 墓地の［プラント］を攻撃力半減で復活',
      effect: (context: TriggerContext) => {
        const success = reviveFromGraveyard(context as any, (card) => {
          return card.type === 'monster' &&
                 hasCategory(card, '【プラント】');
        }, true);

        if (!success) {
          context.addLog('フルーツ・マリオネット・グレープの効果: 墓地に［プラント］モンスターがいません', 'info');
        }
      },
    },
  ],

  /**
   * C0000215: フルーツ・マリオネット・メロン王
   * 【常時】場にいる［プラント］モンスター1体につき攻撃力400アップ。
   */
  C0000215: [
    {
      type: TRIGGER_TYPES.CONTINUOUS,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '常時: ［プラント］1体につき攻撃力+400',
      effect: (context: TriggerContext) => {
        const { monsterIndex } = context;
        const { myField } = getPlayerContext(context);

        // 場の［プラント］をカウント（自分自身を除く）
        const plantCount = myField.filter((monster, idx) =>
          monster &&
          idx !== monsterIndex &&
          hasCategory(monster, '【プラント】')
        ).length;

        if (plantCount > 0) {
          const attackBonus = plantCount * 400;
          modifyAttack(context as any, attackBonus, monsterIndex, false, true);
        }
      },
    },
  ],

  /**
   * C0000218: フルーツ・マリオネット劇場
   * 【常時】光属性［プラント］モンスターの攻撃力を400アップ。
   * 【自分エンドフェイズ時】場に光属性モンスターが3体以上いる場合、相手モンスター1体の攻撃力を500下げ、自分のライフを500回復。
   */
  C0000218: [
    {
      type: TRIGGER_TYPES.CONTINUOUS,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '常時: 光属性［プラント］の攻撃力+400',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        addLog('フルーツ・マリオネット劇場の常時効果: 攻撃力+400（未実装）', 'info');
        // TODO: フィールドカードの常時効果システムが必要
      },
    },
    {
      type: TRIGGER_TYPES.ON_END_PHASE_SELF,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: 'エンドフェイズ: 光属性3体以上で相手攻撃力-500と500回復',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        const { myField, opponentField } = getPlayerContext(context);

        const lightCount = myField.filter((monster) =>
          monster && monster.attribute === '光'
        ).length;

        if (lightCount >= 3) {
          // 相手モンスター1体の攻撃力を500ダウン
          const targetIndex = opponentField.findIndex((m) => m !== null);
          if (targetIndex !== -1) {
            modifyAttack(context as any, -500, targetIndex, true, false);
          }

          // 自分のライフを500回復
          healLife(context as any, 500, true);
          addLog('フルーツ・マリオネット劇場の効果発動', 'info');
        }
      },
    },
  ],

  /**
   * C0000230: 永遠の灯の祈り (Phase Card)
   * 初期効果: 【発動時】場にいる光属性モンスターに【魔障壁（相手の魔法の効果を受けない）】を付与。
   * 1枚重ね: 【自分エンドフェイズ時】光属性モンスター1体のHPを500回復し、相手モンスター1体の攻撃力を400ダウン。
   * 2枚重ね: 【自分光属性モンスター召喚時】そのモンスターのHPを800アップし【魔障壁】付与。
   * 3枚重ね: 自分の光属性モンスター全体のHPを1000アップし、相手フィールド全体に800ダメージを与える。
   */
  C0000230: [
    {
      type: TRIGGER_TYPES.ON_PHASE_CARD_ACTIVATE,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '発動時: 光属性に魔障壁を付与',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        const { myField, setMyField } = getPlayerContext(context);

        // 場の光属性モンスターに魔障壁を付与
        let buffedCount = 0;
        setMyField(prev => {
          return prev.map(monster => {
            if (monster && monster.attribute === '光') {
              buffedCount++;
              return {
                ...monster,
                hasMagicBarrier: true,
              };
            }
            return monster;
          });
        });

        if (buffedCount > 0) {
          addLog(`永遠の灯の祈り【初期効果】: 光属性モンスター${buffedCount}体に魔障壁を付与！`, 'info');
        } else {
          addLog('永遠の灯の祈り【初期効果】: 対象となる光属性モンスターがいません', 'info');
        }
      },
    },
    {
      type: TRIGGER_TYPES.ON_END_PHASE_SELF,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '1枚重ね: 光属性HP+500、相手ATK-400',
      condition: (context: TriggerContext) => {
        const { myPhaseCard } = getPlayerContext(context);
        return myPhaseCard && myPhaseCard.id === 'C0000230' && (myPhaseCard.stage || 0) >= 1;
      },
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        const { myField, setMyField, opponentField, setOpponentField } = getPlayerContext(context);

        // 光属性モンスターのHP+500
        const lightMonsters = myField
          .map((m, i) => ({ monster: m, index: i }))
          .filter(({ monster }) => monster && monster.attribute === '光');

        if (lightMonsters.length > 0) {
          // 自動的に1体選択（最初の光属性モンスター）
          const target = lightMonsters[0];
          setMyField(prev => {
            const newField = [...prev];
            const monster = newField[target.index];
            if (monster) {
              newField[target.index] = {
                ...monster,
                currentHp: (monster.currentHp ?? monster.hp ?? 0) + 500,
                hp: (monster.hp ?? 0) + 500,
              };
            }
            return newField;
          });
          addLog(`永遠の灯の祈り【1枚重ね】: ${target.monster?.name ?? 'モンスター'}のHPを500回復！`, 'heal');
        }

        // 相手モンスター1体のATK-400
        const opponentMonsters = opponentField
          .map((m, i) => ({ monster: m, index: i }))
          .filter(({ monster }) => monster !== null);

        if (opponentMonsters.length > 0) {
          // 自動的に1体選択（最初の相手モンスター）
          const target = opponentMonsters[0];
          setOpponentField(prev => {
            const newField = [...prev];
            const monster = newField[target.index];
            if (monster) {
              const newAttack = Math.max(0, (monster.currentAttack ?? monster.attack ?? 0) - 400);
              newField[target.index] = {
                ...monster,
                currentAttack: newAttack,
              };
            }
            return newField;
          });
          addLog(`永遠の灯の祈り【1枚重ね】: ${target.monster?.name ?? 'モンスター'}の攻撃力を400ダウン！`, 'damage');
        }
      },
    },
    {
      type: TRIGGER_TYPES.ON_ATTRIBUTE_SUMMON_SELF,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '2枚重ね: 光属性召喚時HP+800、魔障壁付与',
      condition: (context: TriggerContext) => {
        const { card } = context;
        const { myPhaseCard } = getPlayerContext(context);
        return card && card.attribute === '光' &&
               myPhaseCard && myPhaseCard.id === 'C0000230' && (myPhaseCard.stage || 0) >= 2;
      },
      effect: (context: TriggerContext) => {
        const { card, addLog, slotIndex } = context;
        const { setMyField } = getPlayerContext(context);

        if (slotIndex !== undefined && slotIndex !== null) {
          setMyField(prev => {
            const newField = [...prev];
            const monster = newField[slotIndex];
            if (monster) {
              newField[slotIndex] = {
                ...monster,
                currentHp: (monster.currentHp ?? monster.hp ?? 0) + 800,
                hp: (monster.hp ?? 0) + 800,
                hasMagicBarrier: true,
              } as MonsterInstance;
            }
            return newField;
          });
          addLog(`永遠の灯の祈り【2枚重ね】: ${card?.name ?? 'モンスター'}のHP+800、魔障壁付与！`, 'info');
        }
      },
    },
  ],

  /**
   * C0000234: 撮影会のリリカ
   * 【常時】相手の魔法カードの発動をターンに1度無効化。
   */
  C0000234: [
    {
      type: TRIGGER_TYPES.CONTINUOUS,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '常時: 相手の魔法を1度無効化',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        addLog('撮影会のリリカの常時効果: 魔法無効化（未実装）', 'info');
        // TODO: 魔法無効化システムの実装が必要
      },
    },
  ],

  /**
   * C0000235: プリンセス狂いのリリカ
   * 【召喚時】デッキから《プリティ☆プリンセス》魔法カード1枚を発動する（コストなし）。
   */
  C0000235: [
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '召喚時: 《プリティ☆プリンセス》をコスト不要で発動',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        addLog('プリンセス狂いのリリカの効果: 《プリティ☆プリンセス》発動（未実装）', 'info');
        // TODO: カード発動システムの実装が必要
      },
    },
  ],

  /**
   * C0000241: ご主人様
   * 【召喚時】SP2を消費してデッキまたは墓地から《リリカ》と名の付くモンスター1体を場に召喚する（攻撃力50％、HP50％）。
   */
  C0000241: [
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.OPTIONAL,
      description: '召喚時: SP2消費で《リリカ》を特殊召喚',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        const { myActiveSP } = getPlayerContext(context);

        if (myActiveSP < 2) {
          addLog('ご主人様の効果: SPが不足しています', 'info');
          return;
        }

        addLog('ご主人様の効果: 《リリカ》特殊召喚（未実装）', 'info');
        // TODO: 特殊召喚システムの実装が必要
      },
    },
  ],

  /**
   * C0000248: 輝鎖の聖姫ルミリア
   * 【召喚時】場にいる光属性モンスター1体につき相手に500ダメージを与え、ターン終了時まで相手モンスター全体の攻撃力を半分にする。
   * 【常時】場に『鎖縛の幻姫リアノン』または『鎖縛の禁忌姫リアノン・エターナル』がいる場合、このカードの攻撃力を1000アップする。
   */
  C0000248: [
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '召喚時: 光属性×500ダメージ、相手全体攻撃力半分',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        const { myField, setOpponentField } = getPlayerContext(context);

        // 場の光属性モンスターをカウント
        const lightCount = myField.filter((monster) =>
          monster && monster.attribute === '光'
        ).length;

        if (lightCount > 0) {
          const damage = lightCount * 500;
          conditionalDamage(context as any, damage, 'opponent');
          addLog(`輝鎖の聖姫ルミリアの効果: 相手に${damage}ダメージ`, 'damage');

          // 相手モンスター全体の攻撃力を半分に
          setOpponentField((prev) => {
            return prev.map((monster) => {
              if (monster) {
                return {
                  ...monster,
                  currentAttack: Math.floor((monster.currentAttack ?? monster.attack ?? 0) / 2),
                  attackModified: true,
                } as MonsterInstance;
              }
              return monster;
            });
          });
          addLog('相手モンスター全体の攻撃力を半分にした', 'info');
        }
      },
    },
    {
      type: TRIGGER_TYPES.CONTINUOUS,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '常時: リアノンいれば攻撃力+1000',
      effect: (context: TriggerContext) => {
        const { monsterIndex } = context;
        const { myField } = getPlayerContext(context);

        const hasRianon = myField.some((monster, idx) =>
          monster &&
          idx !== monsterIndex &&
          monster.name &&
          (monster.name.includes('鎖縛の幻姫リアノン') ||
           monster.name.includes('鎖縛の禁忌姫リアノン・エターナル'))
        );

        if (hasRianon) {
          modifyAttack(context as any, 1000, monsterIndex, false, true);
        }
      },
    },
  ],

  /**
   * C0000314: 聖光フェニックス
   * 【自壊時】次のターンに攻撃力1500で場に戻る（バトル中1度だけ）。
   */
  C0000314: [
    {
      type: TRIGGER_TYPES.ON_DESTROY_SELF,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '破壊時: 次ターンに攻撃力1500で復活',
      effect: (context: TriggerContext) => {
        const { addLog, destroyedCard } = context;
        const { setMyGraveyard, currentPlayer } = getPlayerContext(context);

        // 既に復活済みの場合は発動しない
        if (destroyedCard && (destroyedCard as any).phoenixRevived) {
          addLog('聖光フェニックスの効果: 既に復活済みのため発動しない', 'info');
          return;
        }

        // 墓地のカードに復活フラグを設定
        // 次のターン開始時にmagic-spirit.jsxで処理される
        setMyGraveyard(prev => {
          return prev.map(card => {
            const cardAny = card as any;
            if (card.id === 'C0000314' && !cardAny.pendingRevival && !cardAny.phoenixRevived) {
              addLog('聖光フェニックスの効果: 次のターン開始時に攻撃力1500で復活！', 'info');
              return {
                ...card,
                pendingRevival: {
                  reviveAtTurn: 'nextOwnerTurn',
                  owner: currentPlayer,
                  attack: 1500,
                  phoenixRevived: true,
                },
              };
            }
            return card;
          });
        });
      },
    },
  ],

  /**
   * C0000319: 嵐光の騎士
   * 【自分光属性モンスター攻撃時】相手モンスターに300ダメージを与える。
   * 実装: このカード自身が攻撃時に発動（本来は全光属性モンスター攻撃時だがシステム制約のため）
   */
  C0000319: [
    {
      type: TRIGGER_TYPES.ON_ATTACK,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      displayDescription: '攻撃時',
      description: '攻撃時: 相手モンスター1体に300ダメージ',
      effect: (context: TriggerContext) => {
        const { addLog, setPendingMonsterTarget } = context;
        const { opponentField, setOpponentField, setOpponentGraveyard } = getPlayerContext(context);

        // 相手フィールドのモンスターを確認
        const validTargets = opponentField
          .map((m, idx) => ({ monster: m, index: idx }))
          .filter(t => t.monster !== null);

        if (validTargets.length === 0) {
          addLog('嵐光の騎士の効果: 相手モンスターがいません', 'info');
          return;
        }

        const applyDamage = (targetIndex: number) => {
          setOpponentField(prev => {
            const newField = [...prev];
            const target = newField[targetIndex];
            if (target) {
              const newHp = target.currentHp - 300;
              addLog(`嵐光の騎士の効果: ${target.name}に300ダメージ！`, 'damage');
              if (newHp <= 0) {
                addLog(`${target.name}は破壊された！`, 'damage');
                setOpponentGraveyard(prev => [...prev, target]);
                newField[targetIndex] = null;
              } else {
                newField[targetIndex] = { ...target, currentHp: newHp };
              }
            }
            return newField;
          });
        };

        // 1体のみの場合は自動選択
        if (validTargets.length === 1) {
          applyDamage(validTargets[0].index);
          return;
        }

        // 複数の場合はターゲット選択UI
        if (setPendingMonsterTarget) {
          setPendingMonsterTarget({
            message: '300ダメージを与える相手モンスターを選択',
            targetPlayer: 'opponent',
            callback: applyDamage,
            selectionOwner: context.currentPlayer,
          } as any);
        } else {
          // フォールバック：最初のモンスターに
          applyDamage(validTargets[0].index);
        }
      },
    },
  ],

  /**
   * C0000320: 雷帝ヴォルトロン
   * 【残魂】破壊時、攻撃力-1000で場に戻る（1回のみ）。
   */
  C0000320: [
    {
      type: TRIGGER_TYPES.ON_DESTROY_SELF,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '【残魂】破壊時: 攻撃力-1000で場に戻る',
      effect: (context: TriggerContext) => {
        const monster = context.destroyedCard;
        // 既に残魂を使用している場合は発動しない
        if (monster && (monster as any).zankonUsed) {
          context.addLog(`【残魂】${monster.name}は既に残魂を使用済み`, 'info');
          return;
        }
        executeZankonEffect(context as any, monster as any);
      },
    },
  ],

  /**
   * C0000327: 雷嵐龍サンダーストーム・レックス
   * 【残魂】破壊時、攻撃力-1000で場に戻る（1回のみ）。
   */
  C0000327: [
    {
      type: TRIGGER_TYPES.ON_DESTROY_SELF,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '【残魂】破壊時: 攻撃力-1000で場に戻る',
      effect: (context: TriggerContext) => {
        const monster = context.destroyedCard;
        // 既に残魂を使用している場合は発動しない
        if (monster && (monster as any).zankonUsed) {
          context.addLog(`【残魂】${monster.name}は既に残魂を使用済み`, 'info');
          return;
        }
        executeZankonEffect(context as any, monster as any);
      },
    },
  ],

  /**
   * C0000326: 雷嵐の聖域
   * 【常時】場にいる光属性モンスターの攻撃力を500アップ。
   * 【自分エンドフェイズ時】相手モンスターがいる場合、ランダムに1体を「雷撃」状態にする（次のターン中攻撃力-500、技不能）。
   */
  C0000326: [
    {
      type: TRIGGER_TYPES.CONTINUOUS,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '常時: 光属性の攻撃力+500',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        addLog('雷嵐の聖域の常時効果: 攻撃力+500（未実装）', 'info');
        // TODO: フィールドカードの常時効果システムが必要
      },
    },
    {
      type: TRIGGER_TYPES.ON_END_PHASE_SELF,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: 'エンドフェイズ: 相手1体を「雷撃」状態に',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        const { opponentField } = getPlayerContext(context);

        const opponentMonsters = opponentField.filter((m) => m !== null);
        if (opponentMonsters.length > 0) {
          addLog('雷嵐の聖域の効果: 相手を「雷撃」状態に（未実装）', 'info');
          // TODO: 状態異常システムの実装が必要
        }
      },
    },
  ],

  /**
   * C0000345: ヴォランティス・アルディオン
   * 【召喚時】相手モンスター全体の攻撃力をターン終了時まで500下げる。
   */
  C0000345: [
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '召喚時: 相手モンスター全体の攻撃力-500',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        const { opponentField, setOpponentField } = getPlayerContext(context);

        const opponentMonsters = opponentField.filter((m) => m !== null);
        if (opponentMonsters.length === 0) {
          addLog('ヴォランティス・アルディオンの効果: 相手フィールドにモンスターがいません', 'info');
          return;
        }

        // 相手モンスター全体の攻撃力を500ダウン
        let count = 0;
        setOpponentField((prev) => {
          return prev.map((monster) => {
            if (monster) {
              count++;
              const newAttack = Math.max(0, monster.currentAttack - 500);
              return {
                ...monster,
                currentAttack: newAttack,
                attackModified: true,
              };
            }
            return monster;
          });
        });

        if (count > 0) {
          addLog(`ヴォランティス・アルディオンの効果: 相手モンスター${count}体の攻撃力-500`, 'info');
        }
      },
    },
  ],

  /**
   * C0000346: ヴォランティス・セラヴェント
   * 【召喚時】デッキから《アヴィクルス》魔法カード1枚を手札に加える。
   */
  C0000346: [
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '召喚時: 《アヴィクルス》魔法カードをサーチ',
      effect: (context: TriggerContext) => {
        const found = searchCard(context as any, (card) => {
          return card.type === 'magic' &&
                 Boolean(card.name && card.name.includes('アヴィクルス'));
        });

        if (!found) {
          context.addLog('ヴォランティス・セラヴェントの効果: 対象カードが見つかりませんでした', 'info');
        }
      },
    },
  ],

  /**
   * C0000347: 鳥民の供物者・カルディス
   * 【召喚時】自分の手札1枚を墓地に送り、デッキから《ヴォランティス》モンスター1体を手札に加える。
   */
  C0000347: [
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '召喚時: 手札1枚捨てて《ヴォランティス》サーチ',
      effect: (context: TriggerContext) => {
        const { addLog, setPendingHandSelection } = context;
        const { myHand, myDeck, setMyHand, setMyGraveyard, setMyDeck } = getPlayerContext(context);

        // まずデッキに《ヴォランティス》モンスターがあるかチェック
        const hasVolantisInDeck = myDeck.some((card) =>
          card.type === 'monster' && card.name && card.name.includes('ヴォランティス')
        );

        if (!hasVolantisInDeck) {
          addLog('鳥民の供物者・カルディスの効果: デッキに《ヴォランティス》モンスターがいないため発動できません', 'info');
          return;
        }

        // 手札がない場合も発動不可
        if (myHand.length === 0) {
          addLog('鳥民の供物者・カルディスの効果: 手札がないため発動できません', 'info');
          return;
        }

        // 手札選択UIを表示
        if (setPendingHandSelection) {
          setPendingHandSelection({
            message: '墓地に送る手札を1枚選択してください（カルディスの効果）',
            count: 1,
            callback: (selectedCards) => {
              if (selectedCards.length === 0) {
                addLog('鳥民の供物者・カルディスの効果: 手札が選択されませんでした', 'info');
                return;
              }

              const discardCard = selectedCards[0];
              // 手札から墓地へ
              setMyHand((prev) => prev.filter((c) => c.uniqueId !== discardCard.uniqueId));
              setMyGraveyard((prev) => [...prev, discardCard]);
              addLog(`${discardCard.name}を墓地に送った`, 'info');

              // デッキから《ヴォランティス》モンスターをサーチ
              const volantisIndex = myDeck.findIndex((card) =>
                card.type === 'monster' && card.name && card.name.includes('ヴォランティス')
              );

              if (volantisIndex !== -1) {
                const foundCard = myDeck[volantisIndex];
                setMyDeck((prev) => prev.filter((_, i) => i !== volantisIndex));
                setMyHand((prev) => [...prev, { ...foundCard, uniqueId: foundCard.uniqueId || `${foundCard.id}_${Date.now()}` }]);
                addLog(`鳥民の供物者・カルディスの効果: ${foundCard.name}を手札に加えた！`, 'info');
              }
            },
          });
        } else {
          // setPendingHandSelectionがない場合（シミュレーター等）は自動で最初のカードを選択
          const discardCard = myHand[0];
          setMyHand((prev) => prev.filter((c) => c.uniqueId !== discardCard.uniqueId));
          setMyGraveyard((prev) => [...prev, discardCard]);
          addLog(`${discardCard.name}を墓地に送った`, 'info');

          // デッキから《ヴォランティス》モンスターをサーチ
          const volantisIndex = myDeck.findIndex((card) =>
            card.type === 'monster' && card.name && card.name.includes('ヴォランティス')
          );

          if (volantisIndex !== -1) {
            const foundCard = myDeck[volantisIndex];
            setMyDeck((prev) => prev.filter((_, i) => i !== volantisIndex));
            setMyHand((prev) => [...prev, { ...foundCard, uniqueId: foundCard.uniqueId || `${foundCard.id}_${Date.now()}` }]);
            addLog(`鳥民の供物者・カルディスの効果: ${foundCard.name}を手札に加えた！`, 'info');
          }
        }
      },
    },
  ],

  /**
   * C0000348: ヴォランティス・ファルクェス
   * 【自分《ヴォランティス》モンスターの攻撃時】相手プレイヤーに300ダメージ。
   */
  C0000348: [
    {
      type: TRIGGER_TYPES.ON_ATTACK,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      targetSelf: false, // 自分の全《ヴォランティス》モンスターの攻撃で発火
      displayDescription: '自分《ヴォランティス》モンスター攻撃時',
      description: '《ヴォランティス》攻撃時: 相手に300ダメージ',
      effect: (context: TriggerContext) => {
        const { attacker, addLog } = context;

        // 攻撃者が《ヴォランティス》の場合のみ発火
        if (attacker && attacker.name && attacker.name.includes('ヴォランティス')) {
          addLog('ヴォランティス・ファルクェスの効果: 相手に300ダメージ', 'damage');
          conditionalDamage(context as any, 300, 'opponent');
        }
      },
    },
  ],

  /**
   * C0000350: 鳥民の祈り
   * magic: 自分のライフを800回復し、デッキから《アヴィクルス》フィールドカード1枚を手札に加える。
   * Note: magicカードのためトリガーではなく、発動時効果として実装
   */
  C0000350: [
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '発動時: ライフ800回復、《アヴィクルス》フィールドカードサーチ',
      effect: (context: TriggerContext) => {
        const { addLog } = context;

        // ライフ800回復
        healLife(context as any, 800, true);

        // 《アヴィクルス》フィールドカードをサーチ
        const found = searchCard(context as any, (card) => {
          return card.type === 'field' &&
                 Boolean(card.name && card.name.includes('アヴィクルス'));
        });

        if (!found) {
          addLog('鳥民の祈りの効果: 《アヴィクルス》フィールドカードが見つかりませんでした', 'info');
        }
      },
    },
  ],

  /**
   * C0000351: 天翔峰アヴィクルス
   * 【常時】《ヴォランティス》モンスターの攻撃力を700アップ。
   * 【相手モンスター召喚時】対象の攻撃力をターン終了時まで500下げる。
   * 【エンドフェイズ時】場に《ヴォランティス》モンスターがいる場合、自分のライフを500回復。
   */
  C0000351: [
    {
      type: TRIGGER_TYPES.CONTINUOUS,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '常時: 《ヴォランティス》の攻撃力+700',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        addLog('天翔峰アヴィクルスの常時効果: 攻撃力+700（未実装）', 'info');
        // TODO: フィールドカードの常時効果システムが必要
      },
    },
    {
      type: TRIGGER_TYPES.ON_OPPONENT_SUMMON,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '相手召喚時: 攻撃力-500',
      effect: (context: TriggerContext) => {
        const { slotIndex, addLog } = context;
        modifyAttack(context as any, -500, slotIndex, true, false);
        addLog('天翔峰アヴィクルスの効果: 召喚されたモンスターの攻撃力-500', 'info');
      },
    },
    {
      type: TRIGGER_TYPES.ON_END_PHASE_SELF,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: 'エンドフェイズ: 《ヴォランティス》いれば500回復',
      effect: (context: TriggerContext) => {
        const { myField } = getPlayerContext(context);

        const hasVolantis = myField.some((monster) =>
          monster && monster.name && monster.name.includes('ヴォランティス')
        );

        if (hasVolantis) {
          healLife(context as any, 500, true);
        }
      },
    },
  ],

  /**
   * C0000352: アヴィクルスの試練
   * 初期効果: 【常時】《ヴォランティス》モンスターの攻撃力を400アップ。（continuousEffects/phaseCards.jsで実装済み）
   * 1枚重ね: 【エンドフェイズ時】相手モンスター全体の攻撃力を500ダウン。
   * 2枚重ね: 【自分《ヴォランティス》モンスター召喚時】相手に400ダメージ。
   * 3枚重ね: 全ての相手モンスターを「行動不能」にし、相手の手札上限を2減らす。自分に300ダメージ。
   */
  C0000352: [
    {
      type: TRIGGER_TYPES.CONTINUOUS,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '初期効果: 《ヴォランティス》の攻撃力+400',
      effect: (context: TriggerContext) => {
        // 常時効果は continuousEffects/phaseCards.js で実装済み
      },
    },
    {
      type: TRIGGER_TYPES.ON_END_PHASE_SELF,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '1枚重ね: 相手モンスター全体の攻撃力-500',
      condition: (context: TriggerContext) => {
        // フェイズカードが1枚重ね以上かチェック
        const { myPhaseCard } = getPlayerContext(context);
        return myPhaseCard && myPhaseCard.id === 'C0000352' && (myPhaseCard.stage || 0) >= 1;
      },
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        const { setOpponentField } = getPlayerContext(context);

        let count = 0;
        setOpponentField((prev) => {
          return prev.map((monster) => {
            if (monster) {
              count++;
              const newAttack = Math.max(0, monster.currentAttack - 500);
              return { ...monster, currentAttack: newAttack };
            }
            return monster;
          });
        });

        if (count > 0) {
          addLog(`アヴィクルスの試練【1枚重ね】: 相手モンスター${count}体の攻撃力-500`, 'info');
        }
      },
    },
    {
      type: TRIGGER_TYPES.ON_ATTRIBUTE_SUMMON_SELF,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      displayDescription: '2枚重ね: 《ヴォランティス》召喚時',
      description: '2枚重ね: 相手に400ダメージ',
      condition: (context: TriggerContext) => {
        // 召喚されたカードが《ヴォランティス》で、フェイズカードが2枚重ね以上
        const { card } = context;
        const { myPhaseCard } = getPlayerContext(context);
        return card && Boolean(card.name && card.name.includes('ヴォランティス')) &&
               myPhaseCard && myPhaseCard.id === 'C0000352' && (myPhaseCard.stage || 0) >= 2;
      },
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        conditionalDamage(context as any, 400, 'opponent');
        addLog('アヴィクルスの試練【2枚重ね】: 相手に400ダメージ！', 'damage');
      },
    },
  ],

  /**
   * C0000063: 聖獣フェニックス
   * 【自ライフ3000以下】このカードが墓地にある場合、1度だけ場に戻る（攻撃力2000、HP1500）。
   */
  C0000063: [
    {
      type: TRIGGER_TYPES.ON_MAIN_PHASE_FROM_GRAVEYARD,
      activationType: ACTIVATION_TYPES.OPTIONAL,
      description: 'ライフ3000以下: 攻撃力2000、HP1500で場に戻る（1度のみ）',
      effect: (context: TriggerContext) => {
        const { addLog, card, summoningSickness = false } = context;
        const { myLife, myField, setMyField, setMyGraveyard } = getPlayerContext(context);

        // ライフ条件チェック
        if (myLife > 3000) {
          addLog('聖獣フェニックスの効果: ライフが3000より高いため発動不可', 'info');
          return;
        }

        // 既に復活済みかチェック
        if (card && (card as any).phoenixRevived) {
          addLog('聖獣フェニックスの効果: 既に1度復活済みのため発動不可', 'info');
          return;
        }

        // 空きスロットを探す
        const emptySlot = myField.findIndex((slot) => slot === null);
        if (emptySlot === -1) {
          addLog('聖獣フェニックスの効果: フィールドに空きがありません', 'info');
          return;
        }

        // 墓地からカードを取り出し、フィールドに特殊召喚
        const cardUniqueId = (card as any)?.uniqueId;
        setMyGraveyard((prev) => prev.filter((c) => (c as any).uniqueId !== cardUniqueId));

        const revivedMonster = {
          ...card,
          currentAttack: 2000,
          attack: 2000,
          currentHp: 1500,
          maxHp: 1500,
          hp: 1500,
          canAttack: !summoningSickness, // 召喚酔い設定に依存
          charges: [],
          statusEffects: [],
          phoenixRevived: true, // 復活済みフラグ
        } as unknown as MonsterInstance;

        setMyField((prev) => {
          const newField = [...prev];
          newField[emptySlot] = revivedMonster;
          return newField;
        });

        addLog('聖獣フェニックスの効果: 墓地から攻撃力2000、HP1500で場に戻った！', 'heal');
      },
    },
  ],

  /**
   * C0000374: 虹羽密林の輝甲虫・ルクセリオ
   * 【自分エンドフェイズ時】自分のライフを400回復し、デッキの上から1枚を墓地に送る。
   */
  C0000374: [
    {
      type: TRIGGER_TYPES.ON_END_PHASE_SELF,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: 'エンドフェイズ: 400回復、1枚ミル',
      effect: (context: TriggerContext) => {
        healLife(context as any, 400, true);
        millDeck(context as any, 1);
      },
    },
  ],

  /**
   * C0000378: 虹羽密林の金胞草・ファルネシア
   * 【常時】自分の《虹羽密林》モンスターが召喚されるたび、そのモンスターの攻撃力をターン終了時まで400アップ。
   */
  C0000378: [
    {
      type: TRIGGER_TYPES.ON_ATTRIBUTE_SUMMON_SELF,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      displayDescription: '自分《虹羽密林》モンスター召喚時',
      description: '《虹羽密林》召喚時: 攻撃力+400',
      effect: (context: TriggerContext) => {
        const { card, slotIndex, addLog } = context;

        if (card && card.name && card.name.includes('虹羽密林')) {
          modifyAttack(context as any, 400, slotIndex, false, false);
          addLog(`虹羽密林の金胞草・ファルネシアの効果: ${card.name}の攻撃力+400`, 'info');
        }
      },
    },
  ],

  /**
   * C0000397: 呪術狩りの聖焔騎士レオノーラ
   * 【常時】相手の闇属性モンスターが受けるダメージを400アップ。
   */
  C0000397: [
    {
      type: TRIGGER_TYPES.CONTINUOUS,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '常時: 相手の闇属性が受けるダメージ+400',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        addLog('呪術狩りの聖焔騎士レオノーラの常時効果: ダメージ増加（未実装）', 'info');
        // TODO: ダメージ増加システムの実装が必要
      },
    },
  ],

  // ========================================
  // ヴォランティス・鳥民シリーズ
  // ========================================

  /**
   * C0000356: 鳥民の斥候・ヴェルティス
   * 【召喚時】デッキの上から3枚を見て、うち《アヴィクルス》カード1枚を手札に加え、残りを好きな順番で戻す。
   */
  C0000356: [
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '召喚時: デッキトップ3枚から《アヴィクルス》をサーチ',
      effect: (context: TriggerContext) => {
        const { addLog, setPendingDeckReview } = context;
        const { myDeck, setMyDeck, setMyHand } = getPlayerContext(context);

        if (myDeck.length === 0) {
          addLog('鳥民の斥候・ヴェルティスの効果: デッキがありません', 'info');
          return;
        }

        // デッキの上から3枚を取得
        const topCards = myDeck.slice(0, Math.min(3, myDeck.length));
        const remainingDeck = myDeck.slice(Math.min(3, myDeck.length));

        // 《アヴィクルス》カードがあるかチェック
        const aviculusCards = topCards.filter(c => c.name && c.name.includes('アヴィクルス'));

        if (aviculusCards.length === 0) {
          // アヴィクルスがない場合、カードをそのまま戻す
          addLog('鳥民の斥候・ヴェルティスの効果: 《アヴィクルス》カードがありませんでした', 'info');
          // デッキトップに戻す（順番はそのまま）
          setMyDeck([...topCards, ...remainingDeck]);
          return;
        }

        // 1枚のみの場合は自動選択
        if (aviculusCards.length === 1) {
          const selectedCard = aviculusCards[0];
          const otherCards = topCards.filter(c => c.uniqueId !== selectedCard.uniqueId);
          setMyHand(prev => [...prev, selectedCard]);
          setMyDeck([...otherCards, ...remainingDeck]);
          addLog(`鳥民の斥候・ヴェルティスの効果: 「${selectedCard.name}」を手札に加えた`, 'info');
          return;
        }

        // 複数ある場合はデッキ確認UIを使用
        if (setPendingDeckReview) {
          setPendingDeckReview({
            cards: topCards,
            message: '手札に加える《アヴィクルス》カードを選択',
            filter: (card: CardWithId) => Boolean(card.name && card.name.includes('アヴィクルス')),
            count: 1,
            callback: (selectedCards: CardWithId[]) => {
              if (selectedCards && selectedCards.length > 0) {
                const selectedCard = selectedCards[0];
                const otherCards = topCards.filter(c => c.uniqueId !== selectedCard.uniqueId);
                setMyHand(prev => [...prev, selectedCard] as any);
                setMyDeck([...otherCards, ...remainingDeck]);
                addLog(`鳥民の斥候・ヴェルティスの効果: 「${selectedCard.name}」を手札に加えた`, 'info');
              }
            },
            selectionOwner: context.currentPlayer,
          } as any);
        } else {
          // フォールバック: 最初のアヴィクルスを選択
          const selectedCard = aviculusCards[0];
          const otherCards = topCards.filter(c => c.uniqueId !== selectedCard.uniqueId);
          setMyHand(prev => [...prev, selectedCard]);
          setMyDeck([...otherCards, ...remainingDeck]);
          addLog(`鳥民の斥候・ヴェルティスの効果: 「${selectedCard.name}」を手札に加えた`, 'info');
        }
      },
    },
  ],

  /**
   * C0000357: 風刻の使者・シルヴァリス
   * 【召喚時】自分のデッキの《ヴォランティス》カード1枚を墓地に送り、自分のライフを300回復。
   */
  C0000357: [
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '召喚時: 《ヴォランティス》を墓地に送り300回復',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        const { myDeck, setMyDeck, setMyGraveyard } = getPlayerContext(context);

        // デッキから《ヴォランティス》カードを探す
        const volantisCard = myDeck.find(c => c.name && c.name.includes('ヴォランティス'));

        if (!volantisCard) {
          addLog('風刻の使者・シルヴァリスの効果: デッキに《ヴォランティス》カードがありません', 'info');
          return;
        }

        // デッキから墓地に送る
        setMyDeck(prev => prev.filter(c => c.uniqueId !== volantisCard.uniqueId));
        setMyGraveyard(prev => [...prev, volantisCard]);
        addLog(`風刻の使者・シルヴァリスの効果: 「${volantisCard.name}」を墓地に送った`, 'info');

        // ライフ300回復
        healLife(context as any, 300, true);
      },
    },
  ],

  /**
   * C0000368: 裁定の鳥民・ミナルス
   * 【召喚時】デッキから《ヴォランティス》モンスター1枚を墓地に送り、自分のライフを800回復。
   */
  C0000368: [
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '召喚時: 《ヴォランティス》を墓地に送り800回復',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        const { myDeck, setMyDeck, setMyGraveyard } = getPlayerContext(context);

        // デッキから《ヴォランティス》モンスターを探す
        const volantisMonster = myDeck.find(c =>
          c.type === 'monster' && c.name && c.name.includes('ヴォランティス')
        );

        if (!volantisMonster) {
          addLog('裁定の鳥民・ミナルスの効果: デッキに《ヴォランティス》モンスターがありません', 'info');
          return;
        }

        // デッキから墓地に送る
        setMyDeck(prev => prev.filter(c => c.uniqueId !== volantisMonster.uniqueId));
        setMyGraveyard(prev => [...prev, volantisMonster]);
        addLog(`裁定の鳥民・ミナルスの効果: 「${volantisMonster.name}」を墓地に送った`, 'info');

        // ライフ800回復
        healLife(context as any, 800, true);
      },
    },
  ],

  /**
   * C0000285: 薬師の助手ミミカ
   * 【自分のライフが回復したとき】SPトークン１つをアクティブにする。
   * ※基本技は light.js で実装
   */
  C0000285: [
    {
      type: TRIGGER_TYPES.ON_LIFE_HEAL_SELF,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: 'ライフ回復時: SPトークン1つをアクティブに',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        const { myRestedSP, setMyRestedSP, setMyActiveSP } = getPlayerContext(context);

        // レストSPがあれば1つアクティブに
        if (myRestedSP > 0) {
          setMyRestedSP(prev => prev - 1);
          setMyActiveSP(prev => prev + 1);
          addLog('薬師の助手ミミカの効果: SPトークン1つがアクティブに！', 'info');
        } else {
          addLog('薬師の助手ミミカの効果: アクティブにできるレストSPがありません', 'info');
        }
      },
    },
  ],

  /**
   * C0000321: 電刃の執行者
   * 【自攻撃時】相手モンスターを破壊した場合、もう一度攻撃が可能。
   */
  C0000321: [
    {
      type: TRIGGER_TYPES.ON_ATTACK_SUCCESS,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '攻撃成功時: 相手モンスター破壊で追加攻撃可能',
      effect: (context: TriggerContext) => {
        const { addLog, attackedMonster, triggerCard } = context;
        const { setMyField, currentPlayer } = getPlayerContext(context);

        // 相手モンスターを破壊した場合のみ発動
        if (attackedMonster && attackedMonster.wasDestroyed) {
          // このモンスターの攻撃可能フラグを再度trueに
          setMyField(prev => prev.map((m, i) => {
            if (m && m.uniqueId === triggerCard?.uniqueId) {
              addLog('電刃の執行者の効果: 相手モンスターを破壊！もう一度攻撃可能！', 'info');
              return { ...m, canAttack: true };
            }
            return m;
          }));
        }
      },
    },
  ],

  /**
   * C0000328: 雷鳴の斥候
   * 【召喚時】デッキから《雷》または《嵐》と名の付くコスト3以下のモンスター1体を手札に加える。
   */
  C0000328: [
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '召喚時: デッキから《雷》《嵐》コスト3以下のモンスターをサーチ',
      effect: (context: TriggerContext) => {
        const { addLog, setPendingDeckReview } = context;
        const { myDeck, setMyDeck, setMyHand } = getPlayerContext(context);

        // 《雷》または《嵐》と名の付くコスト3以下のモンスターを検索
        const validCards = myDeck.filter(card =>
          card.type === 'monster' &&
          card.cost <= 3 &&
          (card.name.includes('雷') || card.name.includes('嵐'))
        );

        if (validCards.length === 0) {
          addLog('雷鳴の斥候の効果: 条件に合うカードがデッキにありません', 'info');
          return;
        }

        // デッキレビューUIで選択
        if (setPendingDeckReview) {
          setPendingDeckReview({
            cards: validCards,
            message: '手札に加えるカードを選択（《雷》《嵐》コスト3以下）',
            selectCount: 1,
            callback: (selectedCards: CardWithId[]) => {
              if (selectedCards && selectedCards.length > 0) {
                const selected = selectedCards[0];
                setMyDeck(prev => prev.filter(c => c.uniqueId !== selected.uniqueId));
                setMyHand(prev => [...prev, selected] as any);
                addLog(`雷鳴の斥候の効果: ${selected.name}を手札に加えた`, 'info');
              }
            },
            selectionOwner: context.currentPlayer,
          } as any);
        }
      },
    },
  ],

  /**
   * C0000329: 嵐の雷撃獣
   * 【召喚時】相手モンスター1体を「雷撃」状態にする（次のターン中攻撃力-500、技不能）。
   */
  C0000329: [
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '召喚時: 相手モンスター1体を雷撃状態に',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        const { opponentField, setOpponentField } = getPlayerContext(context);

        // 相手モンスターがいるかチェック
        const targets = opponentField.map((m, i) => ({ monster: m, index: i }))
          .filter(t => t.monster !== null);

        if (targets.length === 0) {
          addLog('嵐の雷撃獣の効果: 相手モンスターがいません', 'info');
          return;
        }

        // 1体なら自動選択、複数ならUI選択
        const applyThunder = (targetIndex: number) => {
          setOpponentField(prev => prev.map((m, i) => {
            if (i === targetIndex && m) {
              addLog(`嵐の雷撃獣の効果: ${m.name}を雷撃状態にした！`, 'info');
              const newEffects = [...(m.statusEffects || [])];
              newEffects.push({
                type: 'THUNDER',
                duration: 1,
                source: 'C0000329',
                sourceName: '嵐の雷撃獣',
              });
              return {
                ...m,
                statusEffects: newEffects,
              };
            }
            return m;
          }));
        };

        if (targets.length === 1) {
          applyThunder(targets[0].index);
        } else if (context.setPendingMonsterTarget) {
          context.setPendingMonsterTarget({
            message: '雷撃状態にする相手モンスターを選択',
            targetPlayer: 'opponent',
            validTargets: targets.map(t => t.index),
            callback: applyThunder,
            selectionOwner: context.currentPlayer,
          } as any);
        } else {
          // フォールバック: 最初のターゲットに適用
          applyThunder(targets[0].index);
        }
      },
    },
  ],

  // ========================================
  // ガーディアンモンスター
  // ========================================

  // ========================================
  // 飛行モンスター
  // ========================================

  /**
   * C0000542: 翼の戦士・スカイランサー
   * 【召喚時】自分の場に他の飛行モンスターがいれば、自分の攻撃力300アップ。
   */
  C0000542: [
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '召喚時: 他の飛行がいれば攻撃力+300',
      effect: (context: TriggerContext) => {
        const { addLog, slotIndex } = context;
        const { myField } = getPlayerContext(context);
        const { hasHikou } = require('../keywordAbilities');

        // 自分以外の飛行モンスターがいるかチェック
        const otherHikouMonsters = myField.filter((m, idx) =>
          m && idx !== slotIndex && hasHikou(m)
        );

        if (otherHikouMonsters.length > 0) {
          modifyAttack(context as any, 300, slotIndex, false, true);
          addLog('翼の戦士・スカイランサーの効果: 他の飛行がいるため攻撃力+300！', 'info');
        } else {
          addLog('翼の戦士・スカイランサーの効果: 他の飛行モンスターがいません', 'info');
        }
      },
    },
  ],

  /**
   * C0000544: 蒼空の王・ヴォランティス
   * 【召喚時】相手の場に飛行モンスターがいなければ、相手プレイヤーに500ダメージ。
   * 注: 【常時】効果は continuousEffects/effectDefinitions/monsterCards.ts で実装
   */
  C0000544: [
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '召喚時: 相手に飛行がいなければ500ダメージ',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        const { opponentField } = getPlayerContext(context);
        const { hasHikou } = require('../keywordAbilities');

        // 相手に飛行モンスターがいるかチェック
        const opponentHikouMonsters = opponentField.filter(m => m && hasHikou(m));

        if (opponentHikouMonsters.length === 0) {
          conditionalDamage(context as any, 500, 'opponent');
          addLog('蒼空の王・ヴォランティスの効果: 相手に飛行がいない！500ダメージ！', 'damage');
        } else {
          addLog('蒼空の王・ヴォランティスの効果: 相手に飛行モンスターがいるため発動しません', 'info');
        }
      },
    },
  ],

  // ========================================
  // ガーディアンモンスター
  // ========================================

  /**
   * C0000437: ホーリーバリア・ガーディアン
   * 【相手エンドフェイズ時】このカードのHPを1000回復（最大HPまで）。
   */
  C0000437: [
    {
      type: TRIGGER_TYPES.ON_OPPONENT_END_PHASE,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '相手エンドフェイズ時: HP1000回復',
      effect: (context: TriggerContext) => {
        const { addLog, slotIndex, card } = context;
        const { myField, setMyField } = getPlayerContext(context);

        // トリガーを持つモンスターを探す
        const monsterIndex = slotIndex !== undefined ? slotIndex :
          myField.findIndex(m => m && m.id === 'C0000437');

        if (monsterIndex === -1 || !myField[monsterIndex]) {
          return;
        }

        const monster = myField[monsterIndex];
        const maxHp = monster.hp || 2600; // 基本HP
        const healAmount = Math.min(1000, maxHp - monster.currentHp);

        if (healAmount > 0) {
          setMyField(prev => {
            const newField = [...prev];
            if (newField[monsterIndex]) {
              newField[monsterIndex] = {
                ...newField[monsterIndex],
                currentHp: Math.min(newField[monsterIndex].currentHp + 1000, maxHp),
              };
            }
            return newField;
          });
          addLog(`ホーリーバリア・ガーディアンの効果: HP${healAmount}回復！`, 'heal');
        } else {
          addLog('ホーリーバリア・ガーディアンの効果: HPは既に最大です', 'info');
        }
      },
    },
  ],

  // ========================================
  // 彷徨い状態解除カード
  // ========================================

  /**
   * C0000572: 目覚めの歌い手
   * 【召喚時】味方モンスター1体の【彷徨い】状態を解除する。
   */
  C0000572: [
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '召喚時: 味方モンスター1体の【彷徨い】状態を解除',
      effect: (context: TriggerContext) => {
        const { addLog, setPendingMonsterTarget, slotIndex } = context;
        const { myField, setMyField } = getPlayerContext(context);

        // 自分フィールドの彷徨い状態のモンスターを検索（自分以外）
        const wanderingMonsters = myField
          .map((m, idx) => ({ monster: m, index: idx }))
          .filter(({ monster, index }) => {
            if (!monster || index === slotIndex) return false;
            return monster.statusEffects?.some(
              (s: { type: string }) => s.type === STATUS_EFFECT_TYPES.WANDERING
            );
          });

        if (wanderingMonsters.length === 0) {
          addLog('目覚めの歌い手の効果: 彷徨い状態の味方モンスターがいません', 'info');
          return;
        }

        const removeWandering = (targetIndex: number) => {
          setMyField((prev) => {
            const newField = [...prev];
            const target = newField[targetIndex];
            if (target) {
              // 彷徨い状態を解除
              statusEffectEngine.removeStatus(target, STATUS_EFFECT_TYPES.WANDERING);
              // 攻撃力を元に戻す（彷徨いで0になっていたため）
              const originalAttack = target.attack ?? target.currentAttack ?? 0;
              newField[targetIndex] = {
                ...target,
                currentAttack: originalAttack,
              };
              addLog(`目覚めの歌い手の効果: ${target.name}の【彷徨い】状態を解除！`, 'heal');
            }
            return newField;
          });
        };

        // 1体のみの場合は自動選択
        if (wanderingMonsters.length === 1) {
          removeWandering(wanderingMonsters[0].index);
          return;
        }

        // 複数いる場合は選択UI
        if (setPendingMonsterTarget) {
          setPendingMonsterTarget({
            message: '【彷徨い】状態を解除する味方モンスターを選択',
            targetPlayer: 'self',
            validTargets: wanderingMonsters.map((m) => m.index),
            callback: removeWandering,
            selectionOwner: context.currentPlayer,
          } as any);
        } else {
          removeWandering(wanderingMonsters[0].index);
        }
      },
    },
  ],
};

/**
 * 光属性カードがトリガー実装を持っているかチェック
 * @param {string} cardId - カードID
 * @returns {boolean} トリガー実装を持っている場合true
 */
export const hasLightCardTrigger = (cardId: string): boolean => {
  const triggers = lightCardTriggers as Record<string, unknown>;
  return cardId !== undefined && cardId !== null && triggers[cardId] !== undefined;
};

/**
 * 光属性カードのトリガーを取得
 * @param {string} cardId - カードID
 * @returns {Array|null} トリガー配列、または null
 */
export const getLightCardTriggers = (cardId: string): TriggerDefinition[] | null => {
  const triggers = lightCardTriggers as unknown as Record<string, TriggerDefinition[]>;
  if (!cardId || !triggers[cardId]) {
    return null;
  }
  return triggers[cardId];
};

/**
 * 実装済み光属性カード数を取得
 * @returns {number} 実装済みカード数
 */
export const getLightCardCount = () => {
  return Object.keys(lightCardTriggers).length;
};
